import { MediaResponse, MediaBaseResponse, MediaGetUrlResponse } from '@/types/media.types';
import { unAuthorizedAxios } from './Network';
import { toast } from '@/components/ui/use-toast';
import axios from 'axios';
import axiosInstance from './axiosInstance';
import { useQuery } from '@tanstack/react-query';

export const getMediaPresignedUrl = async (mediaId: string): Promise<string | undefined> => {
  try {
    const { data } = await axiosInstance.get<MediaGetUrlResponse>(`/media/${mediaId}`);
    return data.url;
  } catch {
    return undefined;
  }
};

/** Presigned URL for a media item. Unlike `getMediaPresignedUrl`, errors surface as `isError`. */
export const useMediaUrl = (mediaId: string | null | undefined, options?: { enabled?: boolean; staleTime?: number }) =>
  useQuery<MediaGetUrlResponse>({
    queryKey: ['media', mediaId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<MediaGetUrlResponse>(`/media/${encodeURIComponent(mediaId!)}`);
      return data;
    },
    enabled: (options?.enabled ?? true) && !!mediaId,
    staleTime: options?.staleTime,
  });

export const uploadMedia = async (
  file: File | null,
  uploadedFor: 'VENDOR' | 'WORK_ORDER_ATTEMPT' | 'DELIVERY_UPDATE' | 'PROCESS_BILLING'
): Promise<MediaBaseResponse | undefined> => {
  if (!file) return undefined;

  try {
    const { data } = await axios.post<MediaResponse>('/media', {
      fileName: file.name,
      mimeType: file.type,
      uploadedFor,
    });

    if (!data.presignedPost) {
      toast({
        description: `Failed to upload ${file.name}. No upload URL received.`,
        duration: 5000,
        variant: 'destructive',
      });
      return undefined;
    }
    const formData = new FormData();
    Object.entries(data.presignedPost.fields ?? {}).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
    formData.append('file', file);

    await unAuthorizedAxios.post(data.presignedPost.url, formData);
    return data.media;
  } catch (err) {
    toast({
      description: `Failed to upload ${file.name}. Please try again.`,
      duration: 5000,
      variant: 'destructive',
    });
    return undefined;
  }
};
